import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { LoggingService } from '../../../core/services/logging.service';

interface DeletionRequest {
  id: string;
  userId: string;
  requestedAt: string;
  scheduledDeletionDate: string;
  status: string;
}

@Component({
  selector: 'app-delete-account',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    MessageModule,
    DialogModule
  ],
  templateUrl: './delete-account.component.html',
  styleUrl: './delete-account.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteAccountComponent implements OnInit {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private router = inject(Router);
  private logger = inject(LoggingService);

  isLoading = signal(false);
  isDeleting = signal(false);
  isCanceling = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  deletionRequest = signal<DeletionRequest | null>(null);

  showConfirmDialog = signal(false);
  confirmationChecked = signal(false);

  private apiUrl = environment.api_host;

  ngOnInit(): void {
    this.loadDeletionRequest();
  }

  private loadDeletionRequest(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.auth.getAccessTokenSilently().subscribe({
          next: (token) => {
            this.http.get<DeletionRequest>(
              `${this.apiUrl}/compliance/account-deletion/status`,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            ).subscribe({
              next: (request) => {
                this.deletionRequest.set(request);
                this.isLoading.set(false);
              },
              error: (error) => {
                // 404 means no pending deletion request
                if (error.status === 404) {
                  this.deletionRequest.set(null);
                } else {
                  this.logger.error('Error loading deletion request:', error);
                  this.errorMessage.set('Failed to load deletion status. Please try again.');
                }
                this.isLoading.set(false);
              }
            });
          },
          error: (error) => {
            this.logger.error('Error getting token:', error);
            this.errorMessage.set('Authentication error. Please try again.');
            this.isLoading.set(false);
          }
        });
      } else {
        this.isLoading.set(false);
      }
    });
  }

  openConfirmDialog(): void {
    this.confirmationChecked.set(false);
    this.showConfirmDialog.set(true);
  }

  closeConfirmDialog(): void {
    this.showConfirmDialog.set(false);
    this.confirmationChecked.set(false);
  }

  requestDeletion(): void {
    if (!this.confirmationChecked()) {
      return;
    }

    this.isDeleting.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.auth.getAccessTokenSilently().subscribe({
          next: (token) => {
            this.http.post<DeletionRequest>(
              `${this.apiUrl}/compliance/account-deletion/request`,
              {},
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            ).subscribe({
              next: (request) => {
                this.deletionRequest.set(request);
                this.successMessage.set(
                  'Account deletion requested. You have 30 days to cancel before permanent deletion.'
                );
                this.isDeleting.set(false);
                this.closeConfirmDialog();
              },
              error: (error) => {
                this.logger.error('Error requesting deletion:', error);
                this.errorMessage.set('Failed to request account deletion. Please try again.');
                this.isDeleting.set(false);
              }
            });
          },
          error: (error) => {
            this.logger.error('Error getting token:', error);
            this.errorMessage.set('Authentication error. Please try again.');
            this.isDeleting.set(false);
          }
        });
      } else {
        this.isDeleting.set(false);
      }
    });
  }

  cancelDeletion(): void {
    this.isCanceling.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.auth.getAccessTokenSilently().subscribe({
          next: (token) => {
            this.http.post(
              `${this.apiUrl}/compliance/account-deletion/cancel`,
              {},
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            ).subscribe({
              next: () => {
                this.deletionRequest.set(null);
                this.successMessage.set('Account deletion cancelled successfully.');
                this.isCanceling.set(false);
              },
              error: (error) => {
                this.logger.error('Error canceling deletion:', error);
                this.errorMessage.set('Failed to cancel account deletion. Please try again.');
                this.isCanceling.set(false);
              }
            });
          },
          error: (error) => {
            this.logger.error('Error getting token:', error);
            this.errorMessage.set('Authentication error. Please try again.');
            this.isCanceling.set(false);
          }
        });
      } else {
        this.isCanceling.set(false);
      }
    });
  }

  getDaysRemaining(): number {
    if (!this.deletionRequest()) return 0;

    const scheduledDate = new Date(this.deletionRequest()!.scheduledDeletionDate);
    const now = new Date();
    const diffTime = scheduledDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  }
}
